import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, TextField, MenuItem, Typography, Chip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Avatar,
    Tabs, Tab, Divider, Stack, Card, CardContent, IconButton, Tooltip,
    LinearProgress, Stepper, Step, StepLabel, StepConnector,
    stepConnectorClasses, Alert, AlertTitle, InputAdornment, CircularProgress,
    Badge, Collapse, ButtonGroup,
} from "@mui/material";
import {
    Search, Refresh, Visibility, CheckCircle, Cancel, LocalShipping,
    Inventory, AttachMoney, HourglassEmpty, CloudUpload, DeleteOutline,
    CalendarToday, Phone, Email, LocationOn, Receipt, FilterList,
    PlayArrow, QrCode2, Label, Download, OpenInNew, Rocket, Refresh as RefreshIcon,
    ContentCopy, ExpandMore, ExpandLess, FlightTakeoff, FiberManualRecord,
    AssignmentReturn, Print, Link, Info,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import { getImageFullUrl } from "../utils/common";
import { useParams } from "react-router-dom";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FLOW = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled", "returned"];

const STATUS_CONFIG = {
    pending: { color: "warning", label: "Pending", icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    confirmed: { color: "info", label: "Confirmed", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    processing: { color: "secondary", label: "Processing", icon: <Inventory sx={{ fontSize: 14 }} /> },
    shipped: { color: "primary", label: "Shipped", icon: <LocalShipping sx={{ fontSize: 14 }} /> },
    delivered: { color: "success", label: "Delivered", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    cancelled: { color: "error", label: "Cancelled", icon: <Cancel sx={{ fontSize: 14 }} /> },
    returned: { color: "error", label: "Returned", icon: <Cancel sx={{ fontSize: 14 }} /> },
};

const PAYMENT_CONFIG = {
    paid: { color: "success", label: "Paid" },
    pending: { color: "warning", label: "Pending" },
    failed: { color: "error", label: "Failed" },
    refunded: { color: "info", label: "Refunded" },
};

const PAYMENT_CONFIG_TYPE = {
    cod: { color: "warning", label: "COD" },
    razorpay: { color: "success", label: "Paid" },
    //   failed:   { color: "error",   label: "Failed" },
    //   refunded: { color: "info",    label: "Refunded" },
};

const TIMELINE_STEPS = [
    { label: "Order Placed", field: "createdAt" },
    { label: "Confirmed", field: "confirmedAt" },
    { label: "Processing", field: "processingAt" },
    { label: "Shipped", field: "shippedAt" },
    { label: "Delivered", field: "deliveredAt" },
];

// shipping status badge colours
const SHIPPING_STATUS_CONFIG = {
    // 🆕 Initial
    new: { color: "default", label: "New" },
    pending: { color: "warning", label: "Pending" },

    // 📦 Pre-shipment
    ready_to_ship: { color: "info", label: "Ready to Ship" },

    // 🚚 Pickup
    pickup_scheduled: { color: "info", label: "Pickup Scheduled" },
    pickup_failed: { color: "error", label: "Pickup Failed" },

    // 🚛 Shipping flow
    shipped: { color: "primary", label: "Shipped" },
    in_transit: { color: "primary", label: "In Transit" },
    out_for_delivery: { color: "secondary", label: "Out for Delivery" },

    // ✅ Final states
    delivered: { color: "success", label: "Delivered" },
    cancelled: { color: "error", label: "Cancelled" },

    // 🔁 Return / failure
    rto: { color: "error", label: "RTO (Return to Origin)" },
    lost: { color: "error", label: "Lost" },

    // ❓ fallback
    unknown: { color: "default", label: "Unknown" },
};
// ─── Styled Components ────────────────────────────────────────────────────────

const StyledConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 17 },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.divider,
        borderTopWidth: 2,
        borderRadius: 1,
    },
}));

const StepIconRoot = styled("div")(({ theme, ownerState }) => ({
    width: 36, height: 36, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, fontWeight: 700,
    border: `2px solid ${ownerState.completed || ownerState.active ? theme.palette.primary.main : theme.palette.divider}`,
    background: ownerState.completed
        ? theme.palette.primary.main
        : ownerState.active
            ? alpha(theme.palette.primary.main, 0.1)
            : theme.palette.background.paper,
    color: ownerState.completed ? "#fff" : ownerState.active ? theme.palette.primary.main : theme.palette.text.disabled,
    transition: "all .25s",
}));

function StepIcon({ active, completed, icon }) {
    return (
        <StepIconRoot ownerState={{ active, completed }}>
            {completed ? <CheckCircle sx={{ fontSize: 18 }} /> : icon}
        </StepIconRoot>
    );
}

const HoverRow = styled(TableRow)(({ theme }) => ({
    cursor: "pointer",
    transition: "background .15s",
    "&:hover": { background: alpha(theme.palette.primary.main, 0.04) },
    "&:last-child td": { border: 0 },
}));

const UploadZone = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.primary.light}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    background: alpha(theme.palette.primary.main, 0.03),
    transition: "all .2s",
    "&:hover": {
        background: alpha(theme.palette.primary.main, 0.07),
        borderColor: theme.palette.primary.main,
    },
    width: "100%",
}));

const ShiprocketCard = styled(Paper)(({ theme }) => ({
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid #f77f00`,
    padding: theme.spacing(2.5),
    boxShadow: "none",
    background: alpha("#f77f00", 0.02),
}));

const InfoRow = ({ label, value, copyable, link, mono }) => {
    const copy = () => { navigator.clipboard.writeText(value); toast.success("Copied!"); };
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.7, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                    variant="body2"
                    fontWeight={500}
                    fontFamily={mono ? "monospace" : undefined}
                    sx={{ wordBreak: "break-all", textAlign: "right" }}
                >
                    {value || "—"}
                </Typography>
                {copyable && value && (
                    <Tooltip title="Copy"><IconButton size="small" onClick={copy}><ContentCopy sx={{ fontSize: 13 }} /></IconButton></Tooltip>
                )}
                {link && value && (
                    <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(value, "_blank")}><OpenInNew sx={{ fontSize: 13 }} /></IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
    v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

const fmtDate = (v) =>
    v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const parseJSON = (str, fallback = []) => {
    try { return typeof str === "string" ? JSON.parse(str) : str || fallback; }
    catch { return fallback; }
};

const initials = (name = "") =>
    (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

// ─── Section Title ────────────────────────────────────────────────────────────

function SectionTitle({ children, icon }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 1.5 }}>
            {icon && <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center" }}>{icon}</Box>}
            <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.2}>
                {children}
            </Typography>
        </Stack>
    );
}

// ─── Order Timeline ───────────────────────────────────────────────────────────

function OrderTimeline({ order }) {
    const curIdx = STATUS_FLOW.indexOf(order.order_status);
    const cancelled = order.order_status === "cancelled" || order.order_status === "returned";

    return (
        <Box>
            <SectionTitle icon={<CalendarToday sx={{ fontSize: 14 }} />}>Order Journey</SectionTitle>

            {cancelled && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    <AlertTitle>Order {order.order_status === "cancelled" ? "Cancelled" : "Returned"}</AlertTitle>
                    {order.cancelReason || "No reason provided."}
                    {(order.cancelledAt || order.returnedAt) && (
                        <Typography variant="caption" display="block" mt={0.5}>
                            At: {fmt(order.cancelledAt || order.returnedAt)}
                        </Typography>
                    )}
                </Alert>
            )}

            <Stepper activeStep={cancelled ? -1 : curIdx} alternativeLabel connector={<StyledConnector />} sx={{ px: 0 }}>
                {TIMELINE_STEPS.map((step, i) => {
                    const date = order[step.field];
                    const completed = !cancelled && i < curIdx;
                    const active = !cancelled && i === curIdx;
                    return (
                        <Step key={step.label} completed={completed}>
                            <StepLabel StepIconComponent={(props) => <StepIcon {...props} icon={i + 1} />}>
                                <Typography variant="caption" fontWeight={active ? 700 : 500} color={active ? "primary.main" : "text.primary"}>
                                    {step.label}
                                </Typography>
                                <Typography variant="caption" display="block" color={date ? "text.secondary" : "text.disabled"} sx={{ fontSize: 10, mt: 0.3, lineHeight: 1.4 }}>
                                    {date ? fmt(date) : "—"}
                                </Typography>
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            <Box sx={{ mt: 3 }}>
                <SectionTitle icon={<Receipt sx={{ fontSize: 14 }} />}>Date Breakdown</SectionTitle>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.50" }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Event</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Date & Time</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[
                                { event: "Order Placed", date: order.createdAt },
                                { event: "Confirmed", date: order.confirmedAt },
                                { event: "Processing", date: order.processingAt },
                                { event: "Shipped", date: order.shippedAt },
                                { event: "Delivered", date: order.deliveredAt },
                                { event: "Cancelled", date: order.cancelledAt },
                                { event: "Returned", date: order.returnedAt },
                            ]
                                .filter((r) => r.date)
                                .map((r) => (
                                    <TableRow key={r.event}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarToday sx={{ fontSize: 13, color: "text.disabled" }} />
                                                <Typography variant="body2" fontWeight={500}>{r.event}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell><Typography variant="body2" color="text.secondary">{fmt(r.date)}</Typography></TableCell>
                                        <TableCell>
                                            <Chip icon={<CheckCircle sx={{ fontSize: "14px !important" }} />} label="Done" color="success" size="small" variant="outlined" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Paper>
            </Box>

            {order.adminNote && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    <AlertTitle sx={{ fontSize: 13 }}>Admin Note</AlertTitle>
                    <Typography variant="body2">{order.adminNote}</Typography>
                </Alert>
            )}
        </Box>
    );
}

// ─── Panel ─────────────────────────────────────────────────────────

function ShiprocketPanel({ order, onRefresh }) {
    const [creating, setCreating] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [couriers, setCouriers] = useState([]);
    const [selectedCourier, setSelectedCourier] = useState("");
    const [showCouriers, setShowCouriers] = useState(false);

    const hasShiprocketOrder = Boolean(order.shiprocket_order_id);
    const hasAWB = Boolean(order.awb_code);
    const hasLabel = Boolean(order.label_url);
    const hasInvoice = Boolean(order.invoice_url);
    const hasTracking = Boolean(order.tracking_url);

    // Create Shiprocket order
    // const createShiprocketOrder = async () => {
    //     setCreating(true);
    //     try {
    //         await apiClient.post(`/api/vendor/orders/${order.id}/shiprocket/create`);
    //         toast.success("Shiprocket order created!");
    //         onRefresh();
    //     } catch (e) {
    //         toast.error(e?.response?.data?.message || "Failed to create Shiprocket order");
    //     } finally { setCreating(false); }
    // };

    // Fetch available couriers
    // const fetchCouriers = async () => {
    //     try {
    //         const res = await apiClient.get(`/api/vendor/orders/${order.id}/shiprocket/couriers`);
    //         setCouriers(res?.data?.data || []);
    //         setShowCouriers(true);
    //     } catch {
    //         toast.error("Failed to fetch couriers");
    //     }
    // };

    // Assign courier & generate AWB
    // const assignCourier = async () => {
    //     if (!selectedCourier) return toast.warn("Select a courier first");
    //     setAssigning(true);
    //     try {
    //         await apiClient.post(`/api/vendor/orders/${order.id}/shiprocket/assign-courier`, { courier_id: selectedCourier });
    //         toast.success("Courier assigned, AWB generated!");
    //         setShowCouriers(false);
    //         onRefresh();
    //     } catch (e) {
    //         toast.error(e?.response?.data?.message || "Failed to assign courier");
    //     } finally { setAssigning(false); }
    // };

    // Sync tracking status from Shiprocket
    // const syncStatus = async () => {
    //     setSyncing(true);
    //     try {
    //         await apiClient.post(`/api/vendor/orders/${order.id}/shiprocket/sync-status`);
    //         toast.success("Tracking status synced!");
    //         onRefresh();
    //     } catch { toast.error("Sync failed"); }
    //     finally { setSyncing(false); }
    // };

    // Cancel Shiprocket order
    // const cancelShiprocket = async () => {
    //     if (!window.confirm("Cancel this Shiprocket shipment?")) return;
    //     setCancelling(true);
    //     try {
    //         await apiClient.post(`/api/vendor/orders/${order.id}/shiprocket/cancel`);
    //         toast.success("Shiprocket shipment cancelled");
    //         onRefresh();
    //     } catch { toast.error("Cancel failed"); }
    //     finally { setCancelling(false); }
    // };

    // Download helpers
    const openUrl = (url) => window.open(url, "_blank");

    const shippingStatus = SHIPPING_STATUS_CONFIG[order.shipping_status] || SHIPPING_STATUS_CONFIG["new"];

    return (
        <Box>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Rocket sx={{ color: "#f77f00", fontSize: 22 }} />
                    <Typography fontWeight={700} fontSize={16}>Shipment Integration</Typography>
                </Stack>
                <Chip
                    label={shippingStatus.label}
                    color={shippingStatus.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                />
            </Stack>

            {/* Shiprocket IDs & Info */}
            <ShiprocketCard sx={{ mb: 2 }}>
                <SectionTitle icon={<QrCode2 sx={{ fontSize: 14 }} />}>Shipment Details</SectionTitle>
                <InfoRow label=" Order ID" value={order.shiprocket_order_id} copyable mono />
                <InfoRow label=" Shipment" value={order.shiprocket_shipment_id} copyable mono />
                <InfoRow label="AWB Code" value={order.awb_code} copyable mono />
                <InfoRow label="Courier" value={order.courier_name} />
                <InfoRow label="Pickup Date" value={order.pickup_date ? fmtDate(order.pickup_date) : null} />
                <InfoRow label="Est. Delivery" value={order.estimated_delivery ? fmtDate(order.estimated_delivery) : null} />
                <InfoRow label="Delivered Date" value={order.delivered_date ? fmtDate(order.delivered_date) : null} />
                <InfoRow label="Tracking URL" value={order.tracking_url} link />
            </ShiprocketCard>

            {/* Package Dimensions */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <SectionTitle>Package Dimensions & Weight</SectionTitle>
                <Grid container spacing={1.5}>
                    {[
                        { label: "Weight (kg)", value: order.shipping_weight || "—" },
                        { label: "Length (cm)", value: order.shipping_length || "—" },
                        { label: "Width (cm)", value: order.shipping_width || "—" },
                        { label: "Height (cm)", value: order.shipping_height || "—" },
                    ].map(({ label, value }) => (
                        <Grid item xs={6} sm={3} key={label}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                                <Typography fontWeight={700} fontSize={16}>{value}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ mb: 2 }}>
                {/* <SectionTitle>Actions</SectionTitle> */}
                <Stack direction="row" flexWrap="wrap" gap={1.5}>

                    {/* Step 1: Create order on Shiprocket */}
                    {/* {!hasShiprocketOrder && (
                        <Button
                            variant="contained"
                            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <Rocket />}
                            disabled={creating}
                            onClick={createShiprocketOrder}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", bgcolor: "#f77f00", "&:hover": { bgcolor: "#e06e00" } }}
                        >
                            {creating ? "Creating…" : "Create on Shiprocket"}
                        </Button>
                    )} */}

                    {/* Step 2: Assign Courier / Get AWB */}
                    {/* {hasShiprocketOrder && !hasAWB && (
                        <Button
                            variant="outlined"
                            startIcon={<LocalShipping />}
                            onClick={fetchCouriers}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Assign Courier & Get AWB
                        </Button>
                    )} */}

                    {/* Label Download */}
                    {hasLabel && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<Label />}
                            onClick={() => openUrl(order.label_url)}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Download Label
                        </Button>
                    )}

                    {/* Invoice Download */}
                    {/* {hasInvoice && (
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<Receipt />}
                            onClick={() => openUrl(order.invoice_url)}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Download Invoice
                        </Button>
                    )} */}

                    {/* Tracking */}
                    {hasTracking && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<OpenInNew />}
                            onClick={() => openUrl(order.tracking_url)}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Track Shipment
                        </Button>
                    )}

                    {/* Sync Status */}
                    {/* {hasShiprocketOrder && (
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                            disabled={syncing}
                            onClick={syncStatus}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            {syncing ? "Syncing…" : "Sync Status"}
                        </Button>
                    )} */}

                    {/* Cancel Shiprocket */}
                    {/* {hasShiprocketOrder && order.shipping_status !== "delivered" && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : <Cancel />}
                            disabled={cancelling}
                            onClick={cancelShiprocket}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            {cancelling ? "Cancelling…" : "Cancel Shipment"}
                        </Button>
                    )} */}
                </Stack>
            </Box>

            {/* Courier Selection Dropdown */}
            {/* <Collapse in={showCouriers}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <SectionTitle icon={<LocalShipping sx={{ fontSize: 14 }} />}>Select Courier</SectionTitle>
                    {couriers.length === 0 ? (
                        <Alert severity="info">No couriers available for this shipment.</Alert>
                    ) : (
                        <>
                            <TextField
                                select fullWidth size="small"
                                label="Choose Courier"
                                value={selectedCourier}
                                onChange={(e) => setSelectedCourier(e.target.value)}
                                sx={{ mb: 2 }}
                            >
                                {couriers.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
                                            <span>{c.courier_name}</span>
                                            <Stack direction="row" spacing={1}>
                                                <Chip label={`₹${c.rate}`} size="small" color="primary" variant="outlined" />
                                                <Chip label={`${c.estimated_delivery_days}d`} size="small" />
                                            </Stack>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    startIcon={assigning ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                                    disabled={!selectedCourier || assigning}
                                    onClick={assignCourier}
                                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                                >
                                    {assigning ? "Assigning…" : "Confirm & Generate AWB"}
                                </Button>
                                <Button variant="outlined" onClick={() => setShowCouriers(false)} sx={{ borderRadius: 2, textTransform: "none" }}>
                                    Cancel
                                </Button>
                            </Stack>
                        </>
                    )}
                </Paper>
            </Collapse> */}

            {/* Tracking Info if AWB exists */}
            {(order?.imeiNumber || order?.serialNumber) && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: alpha("#1976d2", 0.03) }}>
                    <SectionTitle >Product Details</SectionTitle>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" gap={1}>

                        {order.imeiNumber && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">IMEI Number</Typography>
                                {parseJSON(order.imeiNumber)?.map((res, index)=><Typography key={index} fontWeight={600}> {res}</Typography> )}
                                
                            </Box>
                        )}
                        {order.serialNumber && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                               {parseJSON(order.serialNumber)?.map((res, index)=><Typography key={index} fontWeight={600}> {res}</Typography> )}
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {hasAWB && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: alpha("#1976d2", 0.03) }}>
                    <SectionTitle icon={<FlightTakeoff sx={{ fontSize: 14 }} />}>Tracking Summary</SectionTitle>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" gap={1}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">AWB Number</Typography>
                            <Typography fontWeight={700} fontFamily="monospace" fontSize={16}>{order.awb_code}</Typography>
                        </Box>
                        {order.courier_name && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Courier</Typography>
                                <Typography fontWeight={600}>{order.courier_name}</Typography>
                            </Box>
                        )}
                        {order.estimated_delivery && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Est. Delivery</Typography>
                                <Typography fontWeight={600}>{fmtDate(order.estimated_delivery)}</Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* Return / Refund Status */}
            {(order.return_status !== "none" || order.refund_status !== "none") && (
                <Alert
                    severity={order.return_status === "none" ? "info" : "warning"}
                    icon={<AssignmentReturn />}
                    sx={{ borderRadius: 2 }}
                >
                    <AlertTitle>Return & Refund</AlertTitle>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="body2">Return: <strong>{order.return_status}</strong></Typography>
                        <Typography variant="body2">Refund: <strong>{order.refund_status}</strong></Typography>
                    </Stack>
                </Alert>
            )}
        </Box>
    );
}

// ─── Dispatch / Proof Upload ──────────────────────────────────────────────────

function DispatchTab({ selected, onRefresh }) {
    const [updating, setUpdating] = useState(false);
    const [uploadingProof, setUploadingProof] = useState(false);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [existingProofs, setExistingProofs] = useState([]);

    const parsedProofs = useMemo(() => parseJSON(selected?.dispatch_proof, []), [selected]);
    const vendorProofs = useMemo(() => parseJSON(selected?.vendorMediaProff, []), [selected]);
    const parsedItems = useMemo(() => parseJSON(selected?.items, []), [selected]);
    const [devices, setDevices] = useState([]);

    // Create fields based on count
    useEffect(() => {
        const arr = Array.from({ length: parsedItems[0].qty  || 0}, (_, index) => ({
            imeiNumber: "",
            serialNumber: "",
        }));

        setDevices(arr);
    }, [parsedItems]);

    console.log(devices , 'ooo')

    const [formData, setFormData] = useState({
        weight: ".250",
        length: "1",
        width: "1",
        height: "1",
        imeiNumber: '',
        serialNumber: ''
    });
    const [errors, setErrors] = useState({
        weight: "",
        length: "",
        width: "",
        height: "",
        imeiNumber: '',
        serialNumber: ''
    });

    // Handle input change
    const handleChange2 = (index, field, value) => {
        const updated = [...devices];
        updated[index][field] = value;
        setDevices(updated);
    };

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const nextActions = useMemo(() => {
        if (!selected) return [];
        switch (selected.order_status) {
            case "pending":
                return [{ label: "Accept Order", value: "confirmed", color: "primary", icon: <CheckCircle /> }];
            case "confirmed":
                return [{ label: "Start Processing", value: "processing", color: "secondary", icon: <PlayArrow /> }];
            //   case "processing":
            //     return [{ label: "Mark as Shipped",   value: "shipped",    color: "info",      icon: <LocalShipping /> }];
            // case "shipped":
            //     return [{ label: "Mark as Delivered", value: "delivered", color: "success", icon: <CheckCircle /> }];
            default:
                return [];
        }
    }, [selected]);

    const updateStatus = async (id, status) => {
        const data = {
            imeiNumber:JSON.stringify(devices?.map((res)=>res?.imeiNumber)),
            serialNumber:JSON.stringify(devices?.map((res)=>res?.serialNumber))
        }
        console.log(data, 'ooo')
        setUpdating(true);
        try {
            const fd = new FormData();
            fd.append("order_status", status);
            fd.append("imeiNumber", data?.imeiNumber);
            fd.append("serialNumber", data?.serialNumber);
            mediaFiles.forEach((f) => fd.append("dispatch_proof", f));
            await apiClient.put(`/api/vendor/orders/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Order marked as "${status}"`);
            setMediaFiles([]);
            setMediaPreviews([]);
            onRefresh();
        } catch { toast.error("Failed to update order"); }
        finally { setUpdating(false); }
    };

    const validatePackageDetails = (data) => {
        const errors = {};

        if (!data.weight || data.weight <= 0) {
            errors.weight = "Weight is required and must be greater than 0";
        }

        if (!data.length || data.length <= 0) {
            errors.length = "Length is required and must be greater than 0";
        }

        if (!data.width || data.width <= 0) {
            errors.width = "Width is required and must be greater than 0";
        }

        if (!data.height || data.height <= 0) {
            errors.height = "Height is required and must be greater than 0";
        }

        return errors;
    };

    const createOrder = async () => {
        const validationErrors = validatePackageDetails(formData);
        // ❌ Agar error hai to API call mat karo
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);

            toast.error("Please fill all required package details correctly");
            return;
        }
        setUpdating(true);
        try {
            await apiClient.put(`/api/vendor/create-shipment/${selected?.id}`, { ...formData });
            toast.success(`Order marked as process`);
            setMediaFiles([]);
            setMediaPreviews([]);
            onRefresh();
        } catch { toast.error("Failed to update order"); }
        finally { setUpdating(false); }
    }

    const makeAsShip = async () => {
        // 🔥 REQUIRED VALIDATION
        if (!mediaFiles || mediaFiles.length === 0) {
            toast.error("Please upload at least one image");
            return;
        }
        setUpdating(true);
        try {
            const fd = new FormData();
            mediaFiles.forEach((f) => fd.append("dispatch_proof", f));
            await apiClient.put(`/api/vendor/make-as-ship/${selected?.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Order marked as shipped`);
            setMediaFiles([]);
            setMediaPreviews([]);
            onRefresh();
        } catch { toast.error("Failed to update order"); }
        finally { setUpdating(false); }
    }

    // const uploadProofOnly = async () => {
    //     if (!mediaFiles.length) return toast.warn("No files selected");
    //     setUploadingProof(true);
    //     try {
    //         const fd = new FormData();
    //         mediaFiles.forEach((f) => fd.append("dispatch_proof", f));
    //         await apiClient.put(`/api/vendor/orders/${selected.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    //         toast.success("Proof uploaded!");
    //         setMediaFiles([]);
    //         setMediaPreviews([]);
    //         onRefresh();
    //     } catch { toast.error("Upload failed"); }
    //     finally { setUploadingProof(false); }
    // };

    const handleMediaChange = (e) => {
        const files = Array.from(e.target.files);
        // ✅ Filter only images
        const imageFiles = files.filter(file => file.type.startsWith("image/"));

        if (imageFiles.length !== files.length) {
            alert("Only image files are allowed");
        }

        setMediaFiles((prev) => [...prev, ...files]);
        files.forEach((f) => {
            const reader = new FileReader();
            reader.onload = (ev) =>
                setMediaPreviews((prev) => [...prev, { src: ev.target.result, type: f.type, name: f.name }]);
            reader.readAsDataURL(f);
        });
    };

    const removeMedia = (i) => {
        setMediaFiles((prev) => prev.filter((_, idx) => idx !== i));
        setMediaPreviews((prev) => prev.filter((_, idx) => idx !== i));
    };

    return (
        <Box>

            {/* Status Update Buttons */}
            {selected?.order_status === 'pending' && <>
                {devices.map((item, index) => (
                    <div key={index}>
                        <Typography variant="h6" fontWeight={600} mb={2}>
                            Product Details {index + 1}
                        </Typography>

                        <Grid container spacing={2} style={{ marginBottom: '10px' }}>

                            <Grid item size={{ xs: 12, sm: 6, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="IMEI Number"
                                    placeholder="Enter IMEI Number"
                                    value={item.imeiNumber}
                                    onChange={(e) =>
                                        handleChange2(index, "imeiNumber", e.target.value)
                                    }
                                // error={!!errors.imeiNumber}
                                // helperText={errors.imeiNumber}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }} >
                                <TextField
                                    fullWidth
                                    label="Serial Number"
                                    placeholder="Enter Serial Number"
                                    value={item.serialNumber}
                                    onChange={(e) =>
                                        handleChange2(index, "serialNumber", e.target.value)
                                    }
                                // error={!!errors.serialNumber}
                                // helperText={errors.serialNumber}
                                />
                            </Grid>
                        </Grid>
                    </div>))}
            </>}

            {nextActions.length > 0 && (
                <>
                    <SectionTitle>Update Order Status</SectionTitle>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        {nextActions.map((action) => (
                            <Button
                                key={action.value}
                                variant="contained"
                                color={action.color}
                                startIcon={updating ? <CircularProgress size={14} color="inherit" /> : action.icon}
                                disabled={updating}
                                onClick={() => updateStatus(selected.id, action.value)}
                                sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                            >
                                {updating ? "Updating…" : action.label}
                            </Button>
                        ))}
                    </Stack>
                </>
            )}
            {selected?.order_status === 'processing' && !selected?.shiprocket_shipment_id && <>

                {/* Heading */}
                <Typography variant="h6" fontWeight={600} mb={2}>
                    Package Dimensions & Weight
                </Typography>

                <Grid container spacing={2}>

                    {/* Weight */}
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }} >
                        <TextField
                            fullWidth
                            label="Weight (kg)"
                            placeholder="Enter weight"
                            value={formData.weight || ""}
                            onChange={handleChange("weight")}
                            error={!!errors.weight}
                            helperText={errors.weight}
                            type="number"
                            inputProps={{ min: 0 }}
                        />
                    </Grid>

                    {/* Length */}
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }} >
                        <TextField
                            fullWidth
                            label="Length (cm)"
                            placeholder="Enter length"
                            value={formData.length || ""}
                            onChange={handleChange("length")}
                            error={!!errors.length}
                            helperText={errors.length}
                            type="number"
                            inputProps={{ min: 0 }}
                        />
                    </Grid>

                    {/* Width */}
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }} >
                        <TextField
                            fullWidth
                            label="Width (cm)"
                            placeholder="Enter width"
                            value={formData.width || ""}
                            onChange={handleChange("width")}
                            error={!!errors.width}
                            helperText={errors.width}
                            type="number"
                            inputProps={{ min: 0 }}
                        />
                    </Grid>

                    {/* Height */}
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }} >
                        <TextField
                            fullWidth
                            label="Height (cm)"
                            placeholder="Enter height"
                            value={formData.height || ""}
                            onChange={handleChange("height")}
                            error={!!errors.height}
                            helperText={errors.height}
                            type="number"
                            inputProps={{ min: 0 }}
                        />
                    </Grid>

                </Grid>

                <Button variant="contained" color={'info'}

                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", marginTop: 2 }}
                    disabled={updating} onClick={() => {
                        createOrder()
                    }}
                >Package Dimensions Add</Button>
            </>}
            {selected?.order_status === 'processing' && selected?.shiprocket_shipment_id && <>
                {/* Status actions banner */}
                {nextActions.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        No dispatch actions — current status is <strong>{selected.order_status}</strong>.
                    </Alert>
                ) : (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Upload proof images before or after updating the order status.
                    </Alert>
                )}

                {/* Upload Zone */}
                <SectionTitle icon={<CloudUpload sx={{ fontSize: 14 }} />}>Upload Proof Images</SectionTitle>
                <label>
                    <UploadZone component="div" sx={{ mb: 2 }}>
                        <input type="file" accept="image/*" multiple hidden onChange={handleMediaChange} />
                        <CloudUpload sx={{ fontSize: 36, color: "primary.main", mb: 1 }} />
                        <Typography fontWeight={600} color="primary.main">Click to select files</Typography>
                        <Typography variant="caption" color="text.secondary">JPG, PNG, JPGE multiple allowed</Typography>
                    </UploadZone>
                </label>

                {/* Preview Grid */}
                {mediaPreviews.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                            {mediaPreviews.length} file(s) selected
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1.5}>
                            {mediaPreviews.map((p, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        position: "relative", width: 90, height: 90,
                                        borderRadius: 2, overflow: "hidden",
                                        border: "1px solid", borderColor: "divider",
                                    }}
                                >
                                    {p.type.startsWith("video") ? (
                                        <Box component="video" src={p.src} muted sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                        <Box component="img" src={p.src} alt={p.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    )}
                                    <Tooltip title={p.name}>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeMedia(i)}
                                            sx={{ position: "absolute", top: 2, right: 2, bgcolor: "error.main", color: "#fff", p: 0.3, "&:hover": { bgcolor: "error.dark" } }}
                                        >
                                            <DeleteOutline sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    </Tooltip>
                                    {p.type.startsWith("video") && (
                                        <Box sx={{ position: "absolute", bottom: 4, left: 4, bgcolor: "rgba(0,0,0,0.6)", borderRadius: 1, px: 0.5 }}>
                                            <Typography variant="caption" color="#fff" fontSize={10}>VIDEO</Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                        {/* <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            startIcon={uploadingProof ? <CircularProgress size={12} color="inherit" /> : <CloudUpload />}
                            disabled={uploadingProof}
                            onClick={uploadProofOnly}
                            sx={{ mt: 1.5, borderRadius: 2, textTransform: "none" }}
                        >
                            {uploadingProof ? "Uploading…" : "Upload Proof Only"}
                        </Button> */}
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Button variant="contained" color={'info'}
                    startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <LocalShipping />}
                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", marginTop: 2 }}
                    disabled={updating} onClick={() => {
                        makeAsShip()
                    }}
                >   Mark as Shipped</Button>
            </>}


            {/* Existing Proofs */}
            {(parsedProofs.length > 0 || vendorProofs.length > 0) && (
                <Box sx={{ mb: 2 }}>
                    <SectionTitle icon={<Visibility sx={{ fontSize: 14 }} />}>Previously Uploaded Proof</SectionTitle>
                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                        {[...parsedProofs, ...vendorProofs].map((proof, i) => {
                            const url = typeof proof === "string" ? getImageFullUrl(proof) : getImageFullUrl(proof?.url || proof);
                            const isVideo = url?.match(/\.(mp4|mov|webm|avi)$/i);
                            return (
                                <Box
                                    key={i}
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    sx={{ position: "relative", width: 90, height: 90, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider", display: "block", textDecoration: "none" }}
                                >
                                    {isVideo ? (
                                        <Box sx={{ width: "100%", height: "100%", bgcolor: "grey.200", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <PlayArrow sx={{ color: "text.secondary" }} />
                                        </Box>
                                    ) : (
                                        <Box component="img" src={url} alt={`Proof ${i + 1}`} onError={(e) => (e.target.style.display = "none")} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    )}
                                    <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0)", transition: "all .2s", "&:hover": { bgcolor: "rgba(0,0,0,0.3)" }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <OpenInNew sx={{ color: "#fff", fontSize: 18, opacity: 0, ".MuiBox-root:hover &": { opacity: 1 } }} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const OrderDetails = () => {
    const { orderId } = useParams();
    const [activeTab, setActiveTab] = useState(0);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchDetails = useCallback(() => {
        setLoading(true);
        setError("");
        apiClient
            .get(`/api/vendor/orders/${orderId}`)
            .then((res) => setSelected(res?.data?.data))
            .catch((err) => setError(err?.response?.data?.message || "Failed to load order."))
            .finally(() => setLoading(false));
    }, [orderId]);

    useEffect(() => {
        if (!orderId) return;
        fetchDetails();
    }, [orderId, fetchDetails]);

    const parsedItems = useMemo(() => parseJSON(selected?.items, []), [selected]);
    const parsedAddr = useMemo(() => parseJSON(selected?.delivery_address, {}), [selected]);
    console.log(parsedItems, 'ooo')
    if (loading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
    if (!selected) return null;

    const TABS = ["Timeline", "Details", "Items", "Shipment Details"];

    return (
        <Paper sx={{ p: { xs: 2, md: 4 }, mx: "auto" }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={0.5}>
                        <Typography variant="h6" fontWeight={700}>{selected.orderNumber}</Typography>
                        <Chip
                            icon={STATUS_CONFIG[selected.order_status]?.icon}
                            label={STATUS_CONFIG[selected.order_status]?.label}
                            color={STATUS_CONFIG[selected.order_status]?.color}
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                        <Chip
                            label={PAYMENT_CONFIG_TYPE[selected.payment_type]?.label}
                            color={PAYMENT_CONFIG_TYPE[selected.payment_type]?.color}
                            size="small"
                            variant="outlined"
                        />
                        {selected.awb_code && (
                            <Chip
                                icon={<QrCode2 sx={{ fontSize: 13 }} />}
                                label={`AWB: ${selected.awb_code}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontFamily: "monospace", fontWeight: 600 }}
                            />
                        )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {/* {selected.trackingId} */}
                        • {fmt(selected.createdAt)}
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchDetails} size="small"><Refresh /></IconButton>
                </Tooltip>
            </Stack>

            {/* Shiprocket quick-info bar */}
            {selected.awb_code && (
                <Alert
                    severity="info"
                    icon={<Rocket />}
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                        <Stack direction="row" spacing={1}>
                            {selected.label_url && (
                                <Button size="small" startIcon={<Label />} onClick={() => window.open(selected.label_url, "_blank")} sx={{ textTransform: "none" }}>Label</Button>
                            )}
                            {selected.tracking_url && (
                                <Button size="small" startIcon={<OpenInNew />} onClick={() => window.open(selected.tracking_url, "_blank")} sx={{ textTransform: "none" }}>Track</Button>
                            )}
                        </Stack>
                    }
                >
                    Shipment via <strong>{selected.courier_name || "Shiprocket"}</strong> — AWB: <strong style={{ fontFamily: "monospace" }}>{selected.awb_code}</strong>
                </Alert>
            )}

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
                variant="scrollable"
                scrollButtons="auto"
            >
                {TABS.map((t, i) => (
                    <Tab
                        key={t}
                        label={
                            t === "Shiprocket"
                                ? <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Rocket sx={{ fontSize: 14 }} />
                                    <span>{t}</span>
                                    {!selected.shiprocket_order_id && <FiberManualRecord sx={{ fontSize: 10, color: "warning.main" }} />}
                                </Stack>
                                : t
                        }
                        sx={{ fontSize: 13, textTransform: "none", fontWeight: 500 }}
                    />
                ))}
            </Tabs>

            <Box sx={{ mt: 3 }}>
                {/* Tab 0 – Timeline */}
                {activeTab === 0 && <OrderTimeline order={selected} />}

                {/* Tab 1 – Details */}
                {activeTab === 1 && (
                    <Grid container spacing={2}>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                <SectionTitle icon={<Email sx={{ fontSize: 14 }} />}>Customer</SectionTitle>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Avatar sx={{ bgcolor: "primary.light", color: "primary.dark", fontWeight: 700 }}>
                                        {initials(selected.customerName)}
                                    </Avatar>
                                    <Box>
                                        <Typography fontWeight={600}>{selected.customerName}</Typography>
                                        {/* <Typography variant="caption" color="text.secondary">{selected.customerEmail}</Typography> */}
                                    </Box>
                                </Stack>
                                <Divider sx={{ mb: 1.5 }} />
                                {[
                                    // [<Phone sx={{ fontSize: 15 }} />, "Mobile", selected.customerMobile],
                                    [null, "Method", selected.payment_type?.toUpperCase()],
                                    [null, "Payment ID", selected.paymentId],
                                ].filter(([, , v]) => v).map(([icon, label, value]) => (
                                    <Stack key={label} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.7 }}>
                                        {icon && <Box sx={{ color: "text.secondary", mt: 0.2, flexShrink: 0 }}>{icon}</Box>}
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>{label}</Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{value}</Typography>
                                    </Stack>
                                ))}
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                <SectionTitle icon={<AttachMoney sx={{ fontSize: 14 }} />}>Payment Summary</SectionTitle>
                                {[
                                    ["Subtotal", `₹${parseFloat(selected.vendor_total || 0).toLocaleString("en-IN")}`],
                                    // ["Tax", `₹${parseFloat(selected.tax_amount || 0).toLocaleString("en-IN")}`],
                                    ["Shipping Cost", `₹${parseFloat(selected.shipping_cost || 0).toLocaleString("en-IN")}`],
                                    // ["Discount", `-₹${parseFloat(selected.total_discount || 0).toLocaleString("en-IN")}`],
                                    // ["Coupon Discount", `-₹${parseFloat(selected.coupon_discount || 0).toLocaleString("en-IN")}`],
                                ].map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Typography variant="body2">{v}</Typography>
                                    </Stack>
                                ))}
                                <Divider sx={{ my: 1 }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography fontWeight={700}>Grand Total</Typography>
                                    <Typography fontWeight={700} color="primary.main" fontSize={16}>
                                        ₹{parseFloat(selected.vendor_total || 0).toLocaleString("en-IN")}
                                    </Typography>
                                </Stack>
                                <Box mt={1.5}>
                                    <Chip label={PAYMENT_CONFIG[selected.payment_status]?.label} color={PAYMENT_CONFIG[selected.payment_status]?.color} size="small" />
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <SectionTitle icon={<LocationOn sx={{ fontSize: 14 }} />}>Delivery Address</SectionTitle>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <LocationOn sx={{ color: "text.secondary", mt: 0.3, flexShrink: 0 }} />
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                            {parsedAddr.address_line1}{parsedAddr.landmark ? `, ${parsedAddr.landmark}` : ""}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {[parsedAddr.city, parsedAddr.state, parsedAddr.country, parsedAddr.pincode].filter(Boolean).join(", ")}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                                            <Chip label={parsedAddr.type || "Home"} size="small" variant="outlined" />
                                            {/* {parsedAddr.mobile && (
                                                <Chip icon={<Phone sx={{ fontSize: 12 }} />} label={parsedAddr.mobile} size="small" variant="outlined" />
                                            )} */}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <SectionTitle>Shipping Info</SectionTitle>
                                {[
                                    ["Method", selected.shipping_method],
                                    ["Pickup Type", selected.pickupType],
                                    ["Shipping Status", selected.shipping_status],
                                    ["Return Status", selected.return_status],
                                    ["Refund Status", selected.refund_status],
                                ].filter(([, v]) => v).map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{ textTransform: "capitalize" }}>{v}</Typography>
                                    </Stack>
                                ))}
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <SectionTitle>Flags</SectionTitle>
                                {[
                                    ["Is Paid", selected.isPaid],
                                    ["Is Delivered", selected.isDelivered],
                                    ["Return Request", selected.returnRequest],
                                ].map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.6 }}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Chip label={v ? "Yes" : "No"} color={v ? "success" : "default"} size="small" />
                                    </Stack>
                                ))}
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 2 – Items */}
                {activeTab === 2 && (
                    <Stack spacing={2}>
                        {parsedItems.map((item, i) => (
                            <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Stack direction="row" spacing={2}>
                                    {item.thumbnail && (
                                        <Box
                                            component="img"
                                            src={getImageFullUrl(item.thumbnail)}
                                            alt={item.productName}
                                            onError={(e) => (e.target.style.display = "none")}
                                            sx={{ width: 72, height: 72, borderRadius: 2, objectFit: "cover", border: "1px solid", borderColor: "divider", flexShrink: 0 }}
                                        />
                                    )}
                                    <Box flex={1}>
                                        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                            <Box>
                                                <Typography fontWeight={600}>{item.productName}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">SKU: {item.sku}</Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
                                                    {item.attributes?.map((a, j) => (
                                                        <Chip key={j} label={`${a.attributeName}: ${a.attributeValue}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                                    ))}
                                                </Stack>
                                            </Box>
                                            <Box textAlign="right">
                                                <Typography fontWeight={700} fontSize={16}>₹{parseFloat(item.vendorPrice).toLocaleString("en-IN")}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">Qty: {item.qty}</Typography>
                                                <Typography variant="body2" color="primary.main" fontWeight={600} display="block">
                                                    Total: ₹{item.vendorTotal?.toLocaleString("en-IN")}
                                                </Typography>
                                                <Chip
                                                    label={STATUS_CONFIG[item.status]?.label || item.status}
                                                    color={STATUS_CONFIG[item.status]?.color || "default"}
                                                    size="small"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}

                {/* Tab 3 – Shiprocket */}
                {activeTab === 3 && <><DispatchTab selected={selected} onRefresh={fetchDetails} /> <br /><ShiprocketPanel order={selected} onRefresh={fetchDetails} /></>}

                {/* Tab 4 – Dispatch */}
                {/* {activeTab === 4 && <DispatchTab selected={selected} onRefresh={fetchDetails} />} */}
            </Box>
        </Paper>
    );
};

export default OrderDetails;